import {ANALYTICS_CHANNEL, DV360_CHANNEL, ESCALAFY_CHANNEL, SEARCH_CONSOLE_CHANNEL} from '../../channels/channels';
import {
    addChannelFrom, connectionsFromResponse, hasAgencyCredentials, STATUS_DISCONNECTED,
} from '../../agency/connections_v2/platformsModel';

/**
 * Por qué un modal Vincular cuenta no tiene ninguna cuenta para ofrecer, sin React ni red (UD-9).
 *
 * El caso (wa#219): una agencia quiso vincular Search Console a un cliente desde Configuración › Cuentas
 * y el combo decía sólo «No hay resultados.». Nunca había conectado Search Console, que es un acceso
 * aparte del de Google Ads, y ninguna pantalla se lo dijo: escribió a soporte. Cada vacío dice acá su
 * porqué y su salida, y lo usan los dos modales (el del Panel y el clásico).
 */
export const EMPTY_REASON = {
    // La agencia no tiene ningún acceso a la plataforma.
    NOT_CONNECTED: 'not_connected',
    // Los tiene, y todos se cortaron: sus cuentas no se ofrecen hasta reconectar.
    DISCONNECTED: 'disconnected',
    // Un acceso recién hecho todavía está trayendo sus cuentas.
    SYNCING: 'syncing',
    // El acceso anda, y el usuario con el que se conectó no llega a ninguna cuenta.
    NO_ACCOUNTS: 'no_accounts',
    // No se pudo saber: las conexiones no llegaron.
    UNKNOWN: 'unknown',
};

/**
 * ¿El vacío de esta plataforma se explica con los accesos de la agencia? Las planillas y las tiendas
 * no tienen accesos, y en Escalafy la lista son las organizaciones que todavía no son de ningún
 * cliente: vacía puede querer decir que ya están todas vinculadas.
 */
export function explainsEmptyAccounts(channel) {
    return hasAgencyCredentials(channel) && channel !== ESCALAFY_CHANNEL;
}

/**
 * El porqué del vacío, con la respuesta de `GET agency/connections/` (su `data`, o `null` si no llegó).
 * `null` si la plataforma no se explica así (`explainsEmptyAccounts`).
 */
export function emptyAccountsReason(connectionsData, channel) {
    if (!explainsEmptyAccounts(channel)) return null;
    if (!connectionsData) return EMPTY_REASON.UNKNOWN;
    const credentials = connectionsFromResponse(connectionsData).credentials[channel];
    if (credentials.length === 0) return EMPTY_REASON.NOT_CONNECTED;
    if (credentials.some((credential) => credential.is_syncing === true)) return EMPTY_REASON.SYNCING;
    if (credentials.every((credential) => credential.status === STATUS_DISCONNECTED)) return EMPTY_REASON.DISCONNECTED;
    return EMPTY_REASON.NO_ACCOUNTS;
}

/**
 * Las herramientas de Google que piden su propio acceso: quien ya conectó Google Ads cree que vienen
 * incluidas (era la confusión del caso), así que su vacío lo aclara.
 */
export function isSeparateGoogleAccess(channel) {
    return [ANALYTICS_CHANNEL, SEARCH_CONSOLE_CHANNEL, DV360_CHANNEL].includes(channel);
}

/**
 * A dónde lleva el botón principal de cada vacío, o `null` si no tiene. Conectar o sumar un acceso
 * abre el alta en Conexiones cuando esa pantalla sabe abrirla (`?add=`); reconectar va a Conexiones,
 * que muestra el aviso con «Reconectar».
 */
export function emptyActionPath(reason, channel) {
    if (reason === EMPTY_REASON.SYNCING) return null;
    const opensAdd = reason === EMPTY_REASON.NOT_CONNECTED || reason === EMPTY_REASON.NO_ACCOUNTS;
    if (opensAdd && addChannelFrom(`?add=${encodeURIComponent(channel)}`)) {
        return `/agency/conections?add=${encodeURIComponent(channel)}`;
    }
    return '/agency/conections';
}
